/* eslint-disable no-irregular-whitespace */
import { Link, Spacing, Text } from '@vkontakte/vkui';
import darkCover from 'img/vkmusic-bundle/byte/dark.png';
import lightCover from 'img/vkmusic-bundle/byte/light.png';
import React, { memo, useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { emitAnalyticEvent } from 'reactApp/appHelpers/experimentAnalytic';
import { AnalyticEventNames } from 'reactApp/appHelpers/experimentAnalytic/eventNames';
import { vkMusicBundleHelpLink } from 'reactApp/appHelpers/featuresHelpers/features/vkMusicBundle';
import { WhatsNewDialog } from 'reactApp/components/WhatsNewDialog/WhatsNewDialog';
import { isCurrentDarkTheme } from 'reactApp/modules/theme/theme.selectors';
import { type VK_MUSIC_BUNDLE_SOURCE, VK_MUSIC_RADAR_NAMES } from 'reactApp/modules/vkMusic/vkMusic.const';
import type { VkMusicBundleTariffInfo } from 'reactApp/modules/vkMusic/vkMusic.types';
import { ECategoryGa, sendPaymentGa } from 'reactApp/utils/paymentGa';

import styles from './VkMusicErrorModal.css';
import { closeVkMusicErrorModal } from './VkMusicErrorModal.helpers';

export enum VkMusicErrorModalType {
    NOT_AVAILABLE_ALL = 'NOT_AVAILABLE_ALL',
    NOT_AVAILABLE_TRIAL = 'NOT_AVAILABLE_TRIAL',
}
export interface VkMusicErrorModalProps {
    type: VkMusicErrorModalType;
    onClose?: () => void;
    onPrimaryClick?: () => void;
    onSecondaryClick?: () => void;
    onShow?: () => void;
    onCancel?: () => void;
    tariffInfo?: VkMusicBundleTariffInfo;
    source: VK_MUSIC_BUNDLE_SOURCE;
}

const PROPS = {
    [VkMusicErrorModalType.NOT_AVAILABLE_ALL]: {
        title: 'У вас уже есть подписка на VK Музыку',
        content: 'Подключить Mail Space + VK Музыка не выйдет. Но можно подключить Mail Space отдельно',
        buttonText: () => `Подключить Mail Space`,
    },
    [VkMusicErrorModalType.NOT_AVAILABLE_TRIAL]: {
        title: 'За 0 ₽ не выйдет, но можно по‑другому',
        content: 'Похоже, вы уже пробовали подписку на VK Музыку или Mail Space + VK Музыка. Теперь возможность платная — такие условия',
        buttonText: (price) => `Подключить за ${price} ₽`,
    },
};

export const VkMusicErrorModal = memo<VkMusicErrorModalProps>(
    ({ onShow, onPrimaryClick, onSecondaryClick, onClose, onCancel, tariffInfo, type, source }) => {
        const isDarkTheme = useSelector(isCurrentDarkTheme);

        const radarType = type === VkMusicErrorModalType.NOT_AVAILABLE_ALL ? 'no_bundle' : 'no_trial';

        const sendAnalytics = useCallback((action: string, restArgs = {}) => {
            sendPaymentGa({
                action,
                name: `${VK_MUSIC_RADAR_NAMES.ERROR_MODAL}_${radarType}`,
                eventCategory: ECategoryGa.modal,
                offer_source: source,
                ...restArgs,
            });
        }, []);

        useEffect(() => {
            sendAnalytics('show');
            emitAnalyticEvent(AnalyticEventNames.VK_MUSIC_ERROR_SHOW, { type: radarType, source });
            onShow?.();
        }, []);

        const handleOnPrimaryClick = useCallback(() => {
            closeVkMusicErrorModal();

            sendAnalytics('click', { button_name: 'buy' });
            emitAnalyticEvent(AnalyticEventNames.VK_MUSIC_ERROR_BUY, { type: radarType, source });
            onPrimaryClick?.();
        }, []);

        const handleOnSecondaryClick = useCallback(() => {
            sendAnalytics('click', { button_name: 'choose' });
            emitAnalyticEvent(AnalyticEventNames.VK_MUSIC_ERROR_CHOOSE, { type: radarType, source });
            onSecondaryClick?.();
        }, []);

        const handleOnCancel = useCallback(() => {
            sendAnalytics('close');
            emitAnalyticEvent(AnalyticEventNames.VK_MUSIC_ERROR_CLOSE, { type: radarType, source });
            onCancel?.();
        }, []);

        const { title, content, buttonText } = PROPS[type];

        const contentBlock = useMemo(() => {
            return (
                <>
                    <Spacing size={16} />
                    <Text className={styles.contentText}>
                        {content}
                        {type === VkMusicErrorModalType.NOT_AVAILABLE_ALL && vkMusicBundleHelpLink && (
                            <>
                                .<br />
                                <Link target="_blank" href={vkMusicBundleHelpLink}>
                                    Об условиях акции
                                </Link>
                            </>
                        )}
                    </Text>
                    <Spacing size={36} />
                </>
            );
        }, []);

        return (
            <WhatsNewDialog
                className={styles.root}
                imageUrl={isDarkTheme ? darkCover : lightCover}
                imageWrapperClassName={styles.imageWrapper}
                imageClassName={styles.image}
                contentClassName={styles.content}
                title={title}
                content={contentBlock}
                primaryButtonText={buttonText(tariffInfo?.usualPrice)}
                onClick={handleOnPrimaryClick}
                onSecondary={handleOnSecondaryClick}
                secondaryButtonText={'Выбрать другой VK ID'}
                onClose={onClose}
                onDismiss={handleOnCancel}
                imageBgColor="var(--vkui--color_background_modal)"
                buttonTheme="vk"
                dialogSize="tiny"
                qaId="vkmusic_bundle_error_modal"
                isRebranding
            />
        );
    }
);

VkMusicErrorModal.displayName = 'VkMusicErrorModal';
